import React, { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
    AiBrain01Icon,
    CheckmarkCircle02Icon,
    Pdf01Icon,
    Settings02Icon,
    SparklesIcon,
} from 'hugeicons-react';
import FeatureCards, { FeatureCardItem } from './FeatureCards';
import { getResourceIcon } from '../../Data/IconData';
import '../../Styles/Layout/ResourcePreview.css';
import '../../Styles/Layout/Items/quizCard.css';
import '../../Styles/Pricing/QuizShowcase.css';

interface DemoQuestion {
    id: string;
    text: string;
    choices: string[];
    correctIndex: number;
    explanation: string;
}

// Mirrors the in-chat quiz card (components/Layout/Items/quizItem.tsx) with local state.
const QUESTIONS: DemoQuestion[] = [
    {
        id: 'q1',
        text: 'What does compound interest mean?',
        choices: [
            'Interest charged only on the original amount',
            'Interest earned on the original amount and on past interest',
            'A fixed fee added when a loan ends',
        ],
        correctIndex: 1,
        explanation: 'Each period, interest is added to the balance, so the next period earns interest on a bigger amount.',
    },
    {
        id: 'q2',
        text: 'You invest $1,000 at 10% a year, compounded yearly. What is it worth after 2 years?',
        choices: ['$1,100', '$1,200', '$1,210'],
        correctIndex: 2,
        explanation: '$1,000 × 1.10 = $1,100 after year one, then $1,100 × 1.10 = $1,210.',
    },
    {
        id: 'q3',
        text: 'What does a credit score mainly reflect?',
        choices: ['Your income', 'How reliably you repay borrowed money', 'How much you have saved'],
        correctIndex: 1,
        explanation: 'Scores are built from your repayment history, how much credit you use, and how long you have had it.',
    },
    {
        id: 'q4',
        text: 'Which of these is usually a fixed expense?',
        choices: ['Rent', 'Groceries', 'Eating out'],
        correctIndex: 0,
        explanation: 'Rent stays the same each month; groceries and eating out change with how you spend.',
    },
];

const QUIZ_FEATURES: FeatureCardItem[] = [
    { icon: <SparklesIcon />, title: 'Generated for You', text: 'Multiple-choice and true/false questions from any file or chat.' },
    { icon: <Settings02Icon />, title: 'Your Length and Difficulty', text: 'Choose how many questions and how hard they are.' },
    { icon: <CheckmarkCircle02Icon />, title: 'Answers Explained', text: 'Check any answer as you go and see why it is right.' },
    { icon: <AiBrain01Icon />, title: 'Remember More', text: 'Recalling answers makes what you read stick and shows the gaps.' },
];

const QuizShowcase: React.FC = () => {
    const [index, setIndex] = useState(0);
    const [selections, setSelections] = useState<Record<string, number>>({});
    const [revealed, setRevealed] = useState<Record<string, boolean>>({});
    const [isGraded, setIsGraded] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const question = QUESTIONS[index];
    const total = QUESTIONS.length;
    const isLast = index === total - 1;
    const isLocked = (q: DemoQuestion) => isGraded || !!revealed[q.id];
    const canSubmit = !isGraded && QUESTIONS.every(q => selections[q.id] !== undefined || revealed[q.id]);
    const score = QUESTIONS.filter(q => selections[q.id] === q.correctIndex).length;

    const select = (q: DemoQuestion, choice: number) => {
        if (isLocked(q)) return;
        setSelections(prev => ({ ...prev, [q.id]: choice }));
    };

    const grade = () => {
        if (!canSubmit) return;
        setIsGraded(true);
        setShowResults(true);
    };

    const review = () => {
        setShowResults(false);
        setIndex(0);
    };

    const retake = () => {
        setSelections({});
        setRevealed({});
        setIsGraded(false);
        setShowResults(false);
        setIndex(0);
    };

    const locked = isLocked(question);
    const selected = selections[question.id];
    const ratio = score / total;
    const verdict = score === total
        ? 'All correct'
        : `${ratio >= 0.7 ? 'Nice work' : ratio >= 0.4 ? 'Getting there' : 'Worth another go'} · ${total - score} missed`;

    return (
        <section id="quiz-generation-showcase" className="quiz-showcase" aria-labelledby="quiz-showcase-heading">
            <div className="showcase-header">
                <h2 id="quiz-showcase-heading">Quiz Yourself on Anything</h2>
                <p>Ask Buddy to quiz you on a document or a conversation, then answer right in the chat.</p>
            </div>

            <div className="quiz-demo">
                <div className="quiz-demo-request">
                    <div className="quiz-demo-file">
                        <span className="quiz-demo-file-icon" aria-hidden><Pdf01Icon size={18} /></span>
                        <span className="quiz-demo-file-text">
                            <span className="quiz-demo-file-name">Personal Finance Guide.pdf</span>
                            <span className="quiz-demo-file-meta">PDF · 1.8 MB</span>
                        </span>
                    </div>
                    <p className="quiz-demo-bubble">Quiz me on this. 4 questions, medium difficulty.</p>
                </div>

                <div className="quiz-card quiz-demo-card">
                    <div className="resource-preview-header quiz-card-header">
                        <div className="resource-preview-header-left">
                            {getResourceIcon('quiz')}
                            <h1>Personal Finance Basics</h1>
                        </div>
                    </div>

                    {showResults ? (
                        <div className="quiz-card-body">
                            <div className="quiz-card-results">
                                <div className="quiz-card-score">
                                    <span className="quiz-card-score-value">{score}/{total}</span>
                                    <span className="quiz-card-verdict" data-perfect={score === total}>{verdict}</span>
                                </div>
                                <div className="quiz-card-dots" aria-hidden>
                                    {QUESTIONS.map(q => (
                                        <span
                                            key={q.id}
                                            className="quiz-card-dot"
                                            data-result={selections[q.id] === q.correctIndex ? 'correct' : 'incorrect'}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="quiz-card-pills">
                                <button type="button" className="quiz-card-pill" onClick={review}>Review Answers</button>
                                <button type="button" className="quiz-card-pill" onClick={retake}>Retake</button>
                            </div>
                        </div>
                    ) : (
                        <div className="quiz-card-body" key={question.id}>
                            <p className="quiz-card-question">{question.text}</p>
                            <div className="quiz-card-choices" role="radiogroup" aria-label={question.text}>
                                {question.choices.map((choice, i) => {
                                    const state = !locked
                                        ? (selected === i ? 'selected' : 'idle')
                                        : i === question.correctIndex
                                            ? 'correct'
                                            : selected === i ? 'incorrect' : 'dimmed';
                                    return (
                                        <button
                                            key={choice}
                                            type="button"
                                            role="radio"
                                            aria-checked={selected === i}
                                            className="quiz-card-choice"
                                            data-state={state}
                                            disabled={locked}
                                            onClick={() => select(question, i)}
                                        >
                                            <span>{choice}</span>
                                            {state === 'correct' && <Check size={16} aria-label="Correct answer" />}
                                            {state === 'incorrect' && <X size={16} aria-label="Your answer, incorrect" />}
                                        </button>
                                    );
                                })}
                            </div>
                            {locked && <p className="quiz-card-explanation">{question.explanation}</p>}

                            <div className="quiz-card-footer">
                                <div className="quiz-card-dots" aria-label={`Question ${index + 1} of ${total}`}>
                                    {QUESTIONS.map((q, i) => (
                                        <span
                                            key={q.id}
                                            className="quiz-card-dot"
                                            data-current={i === index}
                                            data-answered={selections[q.id] !== undefined || !!revealed[q.id]}
                                            data-result={isGraded && i !== index ? (selections[q.id] === q.correctIndex ? 'correct' : 'incorrect') : undefined}
                                        />
                                    ))}
                                </div>
                                <div className="quiz-card-actions">
                                    {!locked && (
                                        <button
                                            type="button"
                                            className="quiz-card-link"
                                            onClick={() => setRevealed(prev => ({ ...prev, [question.id]: true }))}
                                        >
                                            Show Answer
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="quiz-card-nav"
                                        onClick={() => setIndex(i => Math.max(i - 1, 0))}
                                        disabled={index === 0}
                                        aria-label="Previous question"
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    {isLast ? (
                                        isGraded ? (
                                            <button type="button" className="quiz-card-grade" onClick={() => setShowResults(true)}>
                                                See Score
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="quiz-card-grade"
                                                onClick={grade}
                                                disabled={!canSubmit}
                                                title={canSubmit ? undefined : 'Answer every question to grade'}
                                            >
                                                Grade
                                            </button>
                                        )
                                    ) : (
                                        <button
                                            type="button"
                                            className="quiz-card-nav"
                                            onClick={() => setIndex(i => Math.min(i + 1, total - 1))}
                                            aria-label="Next question"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <FeatureCards items={QUIZ_FEATURES} label="What quizzes do" />
        </section>
    );
};

export default QuizShowcase;
